// Developer Platform (tenant admin) API client.
//
// Three surfaces behind the gateway (docs/developer-platform/architecture.md):
//  - /api/v1/developer/*       auth-service   — OAuth API clients + scopes (live now)
//  - /api/v1/webhooks-admin/*  event-processor — webhook subscriptions + deliveries
//  - /api/v1/integrations/*    migration-service — connector catalog + connections
//
// The webhook and integration backends ship in parallel with this UI; the tabs
// degrade gracefully (see isNotRolledOut) when the gateway can't route yet.
import { gatewayApi } from '@/lib/hrApi';
import { isEntitlementUnavailable } from '@/lib/apiError';

// ---------------------------------------------------------------- helpers
/** True when a management surface isn't deployed/routed yet (gateway 404/502
 * family) — the tab shows a "being rolled out" empty state, not an error.
 * ⚠️ Not the gateway's 503 entitlement fallback: that is "try again"
 * (isEntitlementUnavailable), not a rollout. */
export const isNotRolledOut = (e: any): boolean => {
  if (isEntitlementUnavailable(e)) return false;
  const s = e?.response?.status;
  return s === 404 || s === 501 || s === 502 || s === 503 || s === 504;
};

/** True when the gateway BLOCKED the request because the tenant isn't entitled to
 * the Developer Platform on its current plan/billing status (403 with the
 * entitlement middleware's code/mode). The backend `message` is user-ready, so the
 * UI surfaces it verbatim rather than a generic error. */
export const isNotEntitled = (e: any): boolean => {
  if (e?.response?.status !== 403) return false;
  const d = e?.response?.data || {};
  return d.mode === 'BLOCK' || d.code === 'feature_not_enabled' || d.code === 'developer_api_not_allowed';
};

// ================================================================ API clients
export interface ApiClient {
  id: string;
  name: string;
  description?: string | null;
  clientId: string;
  scopes: string[];
  /** Actor kind (Agent Platform): app | agent | cli — server-assigned at creation. */
  kind?: 'app' | 'agent' | 'cli' | string;
  environment: 'live' | 'sandbox' | string;
  status: 'active' | 'disabled' | string;
  rateLimitPerMin?: number;
  lastUsedAt?: string | null;
  secretRotatedAt?: string | null;
  createdAt?: string;
}

const DEV_BASE = '/api/v1/developer';

/** Backends may return scopes as strings or {key} objects — normalize. */
const normalizeScopes = (raw: any[]): string[] =>
  (raw || [])
    .map((s: any) => (typeof s === 'string' ? s : s?.key || s?.scope || s?.name || ''))
    .filter(Boolean);

export const listApiClients = async (): Promise<ApiClient[]> => {
  const { data } = await gatewayApi.get<{ clients: ApiClient[] }>(`${DEV_BASE}/clients`);
  return data.clients || [];
};

export const listApiScopes = async (): Promise<string[]> => {
  const { data } = await gatewayApi.get<{ scopes: any[] }>(`${DEV_BASE}/scopes`);
  return normalizeScopes(data.scopes || []);
};

/** Create a client. The response carries the ONE-TIME clientSecret — show it
 * immediately; it is never retrievable again. */
export const createApiClient = async (input: {
  name: string;
  description?: string;
  scopes: string[];
  environment?: string;
  kind?: string;
  acknowledgeSensitive?: boolean;
}): Promise<{ client: ApiClient; clientSecret: string }> => {
  const { data } = await gatewayApi.post<any>(`${DEV_BASE}/clients`, input);
  return {
    client: (data.client ?? data) as ApiClient,
    clientSecret: data.clientSecret ?? data.client_secret ?? '',
  };
};

export const updateApiClientScopes = async (
  id: string,
  scopes: string[],
  acknowledgeSensitive?: boolean,
): Promise<void> => {
  await gatewayApi.put(`${DEV_BASE}/clients/${id}/scopes`, { scopes, acknowledgeSensitive });
};

/** Rotate the secret (old secret keeps working for 24h). One-time response. */
export const rotateApiClientSecret = async (id: string): Promise<string> => {
  const { data } = await gatewayApi.post<any>(`${DEV_BASE}/clients/${id}/rotate-secret`);
  return data.clientSecret ?? data.client_secret ?? '';
};

export const setApiClientEnabled = async (id: string, enabled: boolean): Promise<void> => {
  await gatewayApi.post(`${DEV_BASE}/clients/${id}/${enabled ? 'enable' : 'disable'}`);
};

// ============================================================ scope catalog
export interface ScopeCatalogEntry {
  scope: string;
  description: string;
  sensitive: boolean;
}
export interface ScopeCategory {
  category: string;
  scopes: ScopeCatalogEntry[];
}

/** The rich, data-driven scope catalog (H4) — grouped by category with per-scope
 * descriptions + a `sensitive` flag. Falls back to the PUBLIC catalog (universal-LLM
 * Phase 2: /public/v1/oauth/scopes needs no admin permission), then to an empty list —
 * so consent screens can describe scopes for non-admin users too. */
export const getScopeCatalog = async (): Promise<ScopeCategory[]> => {
  try {
    const { data } = await gatewayApi.get<{ categories?: ScopeCategory[] }>(`${DEV_BASE}/scopes`);
    if (data.categories?.length) return data.categories;
  } catch {
    // fall through to the public catalog
  }
  const { data } = await gatewayApi.get<{ scopes?: (ScopeCatalogEntry & { category: string })[] }>(
    '/public/v1/oauth/scopes',
  );
  const byCat = new Map<string, ScopeCatalogEntry[]>();
  (data.scopes || []).forEach(({ category, ...s }) => {
    if (!byCat.has(category)) byCat.set(category, []);
    byCat.get(category)!.push(s);
  });
  return [...byCat.entries()].map(([category, scopes]) => ({ category, scopes }));
};

// ================================================================= usage (M5)
export interface UsageSummary {
  windowDays: number;
  total: number;
  byDay: { day: string; count: number }[];
  byClient: { clientId: string; count: number }[];
  byStatus: { statusClass: string; count: number }[];
  byRoute: { route: string; count: number }[];
}

/** Aggregated public-API usage for the tenant over the last `days` (default 30). */
export const getUsage = async (days = 30): Promise<UsageSummary> => {
  const { data } = await gatewayApi.get<UsageSummary>(`${DEV_BASE}/usage`, { params: { days } });
  return data;
};

// ========================================================= developer applications
// OAuth authorization_code + PKCE applications (H5): apps that authorize on behalf of
// a signed-in USER (distinct from client-credentials API clients). Public clients rely on
// PKCE alone; confidential clients also hold a secret (shown once at creation).
export interface DeveloperApplication {
  id: string;
  name: string;
  description?: string | null;
  clientId: string;
  confidential: boolean;
  redirectUris: string[];
  scopes: string[];
  environment: 'live' | 'sandbox' | string;
  status: 'active' | 'disabled' | string;
  certificationStatus?: 'none' | 'in_review' | 'certified' | 'rejected' | string;
  certifiedAt?: string | null;
  lastUsedAt?: string | null;
  createdAt?: string;
}

const APP_BASE = `${DEV_BASE}/applications`;

// ---- certification (Agent Platform Phase 9B) --------------------------------

export interface CertificationEvidence {
  ranAt?: string;
  passed?: boolean;
  subscriptionId?: string;
  checks?: {
    checks?: Record<string, { passed: boolean; statusCode?: number; detail?: string; error?: string }>;
    passed?: boolean;
  };
}

export const runCertificationChecks = async (
  appId: string, subscriptionId: string,
): Promise<CertificationEvidence> => {
  const { data } = await gatewayApi.post<{ evidence: CertificationEvidence }>(
    `${APP_BASE}/${appId}/certification/run-checks`, { subscriptionId },
  );
  return data.evidence;
};

export const requestCertification = async (appId: string): Promise<void> => {
  await gatewayApi.post(`${APP_BASE}/${appId}/certification/request`, {});
};

export const listApplications = async (): Promise<DeveloperApplication[]> => {
  const { data } = await gatewayApi.get<{ applications: DeveloperApplication[] }>(APP_BASE);
  return data.applications || [];
};

/** Create an application. A confidential app returns its ONE-TIME clientSecret. */
export const createApplication = async (input: {
  name: string;
  description?: string;
  redirect_uris: string[];
  scopes: string[];
  environment?: string;
  confidential?: boolean;
  acknowledgeSensitive?: boolean;
  kind?: string;
}): Promise<{ application: DeveloperApplication; clientSecret?: string }> => {
  const { data } = await gatewayApi.post<any>(APP_BASE, input);
  return {
    application: (data.application ?? data) as DeveloperApplication,
    clientSecret: data.clientSecret ?? data.client_secret,
  };
};

export const disableApplication = async (id: string): Promise<void> => {
  await gatewayApi.post(`${APP_BASE}/${id}/disable`);
};

// ---- Per-user authorized apps (universal-LLM Phase 2) ----------------------
// Each row is one OAuth connection the signed-in user granted (a refresh-token
// family); revoking cuts that agent/app off at its next refresh.

export interface MyAuthorization {
  familyId: string;
  clientId: string;
  appName: string;
  kind: string;
  source: string;
  certified: boolean;
  scopes: string[];
  grantType: string;
  lastRefreshedAt: string;
  expiresAt: string;
}

export interface ConsentAppInfo {
  clientId: string;
  name: string;
  kind: string;
  platformLevel: boolean;
  source: string;
  certified: boolean;
  scopes: string[];
}

/** Consent-screen lookup: name + provenance of the app asking for access. */
export const getConsentAppInfo = async (clientId: string): Promise<ConsentAppInfo> => {
  const { data } = await gatewayApi.get<ConsentAppInfo>(`${APP_BASE}/info/${clientId}`);
  return data;
};

export const listMyAuthorizations = async (): Promise<MyAuthorization[]> => {
  const { data } = await gatewayApi.get<{ authorizations: MyAuthorization[] }>(
    `${APP_BASE}/authorizations/mine`,
  );
  return data.authorizations || [];
};

export const revokeMyAuthorization = async (familyId: string): Promise<void> => {
  await gatewayApi.post(`${APP_BASE}/authorizations/${familyId}/revoke`);
};

/** OAuth /authorize: the signed-in user grants an application a PKCE-bound code.
 * Returns the built redirect URL the consent screen navigates the browser to. */
export const authorizeApplication = async (input: {
  client_id: string;
  redirect_uri: string;
  scope?: string;
  code_challenge: string;
  code_challenge_method?: string;
  state?: string;
  resource?: string;
}): Promise<{ code: string; redirectTo: string; scopes: string[] }> => {
  const { data } = await gatewayApi.post<any>(`${APP_BASE}/authorize`, input);
  return { code: data.code, redirectTo: data.redirectTo, scopes: data.scopes || [] };
};

// RFC 8628 device flow (Agent Platform Phase 8): the /activate page looks a
// user code up, then the signed-in user approves or denies it.
export type DeviceLookup = {
  userCode: string;
  status: 'pending' | 'approved' | 'denied';
  application: { name: string; clientId: string; kind: string };
  scopes: string[];
  expiresAt: string;
};

export const lookupDeviceCode = async (userCode: string): Promise<DeviceLookup> => {
  const { data } = await gatewayApi.get<DeviceLookup>(
    `${APP_BASE}/device/${encodeURIComponent(userCode)}`,
  );
  return data;
};

export const decideDeviceCode = async (
  userCode: string,
  approve: boolean,
): Promise<{ userCode: string; status: string }> => {
  const { data } = await gatewayApi.post<any>(`${APP_BASE}/device/approve`, {
    user_code: userCode,
    approve,
  });
  return data;
};

// ================================================================== Webhooks
export type WebhookSubscriptionStatus = 'active' | 'failing' | 'disabled';
export type WebhookDeliveryStatus = 'pending' | 'delivered' | 'failed' | 'dead';

export interface WebhookSubscription {
  id: string;
  url: string;
  description?: string | null;
  eventTypes: string[];
  status: WebhookSubscriptionStatus | string;
  consecutiveFailures?: number;
  createdAt?: string;
  /** e.g. "whsec_…a1b2" — never the full secret. */
  secretHint?: string | null;
}

export interface WebhookDeliveryAttempt {
  attemptNo?: number;
  requestedAt?: string;
  durationMs?: number;
  statusCode?: number | null;
  error?: string | null;
}

export interface WebhookDelivery {
  id: string;
  eventId: string;
  eventType: string;
  status: WebhookDeliveryStatus | string;
  attempts: number;
  createdAt?: string;
  deliveredAt?: string | null;
  attempts_detail?: WebhookDeliveryAttempt[];
}

/** Fallback event catalog, used ONLY if the live endpoint is unreachable.
 * The authoritative list is GET /api/v1/webhooks-admin/event-types (served
 * from the same catalog the dispatcher enforces) — fetched by
 * listWebhookEventTypes below. This constant previously drifted 15 types
 * behind the server; never extend it by hand again. */
export const WEBHOOK_EVENT_TYPES_FALLBACK: string[] = [
  '*',
  'employee.created',
  'employee.updated',
  'employee.terminated',
  'leave.requested',
  'leave.approved',
  'leave.rejected',
  'payroll.run.created',
  'payroll.run.approved',
  'payroll.completed',
  'payslip.generated',
  'onboarding.started',
  'onboarding.completed',
  'offboarding.started',
  'offboarding.completed',
  'report.generated',
  'integration.sync.completed',
  'integration.sync.failed',
  'attendance.imported',
  'attendance.overtime.approved',
  'attendance.payroll_ready',
  'benefit.enrollment.approved',
  'benefit.enrollment.terminated',
  'benefit.plan.activated',
];

/** The live public event taxonomy (+ '*'), from the enforcing catalog. */
export const listWebhookEventTypes = async (): Promise<string[]> => {
  try {
    const { data } = await gatewayApi.get<any>('/api/v1/webhooks-admin/event-types');
    const types: string[] = Array.isArray(data) ? data : data?.data;
    if (Array.isArray(types) && types.length) return ['*', ...types];
  } catch {
    // fall through to the static fallback
  }
  return WEBHOOK_EVENT_TYPES_FALLBACK;
};

const WH_BASE = '/api/v1/webhooks-admin';

export const listWebhookSubscriptions = async (): Promise<WebhookSubscription[]> => {
  const { data } = await gatewayApi.get<any>(`${WH_BASE}/subscriptions`);
  return (Array.isArray(data) ? data : data.subscriptions) || [];
};

/** Create a subscription. Response carries the ONE-TIME signing secret. */
export const createWebhookSubscription = async (input: {
  url: string;
  description?: string;
  eventTypes: string[];
}): Promise<{ subscription: WebhookSubscription; secret: string }> => {
  const { data } = await gatewayApi.post<any>(`${WH_BASE}/subscriptions`, input);
  return {
    subscription: (data.subscription ?? data) as WebhookSubscription,
    secret: data.secret ?? data.signingSecret ?? '',
  };
};

export const updateWebhookSubscription = async (
  id: string,
  patch: { url?: string; description?: string; eventTypes?: string[]; status?: string },
): Promise<void> => {
  await gatewayApi.patch(`${WH_BASE}/subscriptions/${id}`, patch);
};

export const deleteWebhookSubscription = async (id: string): Promise<void> => {
  await gatewayApi.delete(`${WH_BASE}/subscriptions/${id}`);
};

/** Rotate the signing secret. One-time response. */
export const rotateWebhookSecret = async (id: string): Promise<string> => {
  const { data } = await gatewayApi.post<any>(`${WH_BASE}/subscriptions/${id}/rotate-secret`);
  return data.secret ?? data.signingSecret ?? '';
};

/** Send a signed ping event to the endpoint. */
export const testWebhookSubscription = async (
  id: string,
): Promise<{ statusCode: number; durationMs: number }> => {
  const { data } = await gatewayApi.post<{ statusCode: number; durationMs: number }>(
    `${WH_BASE}/subscriptions/${id}/test`,
  );
  return data;
};

export const listWebhookDeliveries = async (
  id: string,
  limit = 25,
): Promise<WebhookDelivery[]> => {
  const { data } = await gatewayApi.get<{ deliveries: WebhookDelivery[] }>(
    `${WH_BASE}/subscriptions/${id}/deliveries`,
    { params: { limit } },
  );
  return data.deliveries || [];
};

export const replayWebhookDelivery = async (deliveryId: string): Promise<void> => {
  await gatewayApi.post(`${WH_BASE}/deliveries/${deliveryId}/replay`);
};

// ============================================================== Integrations
export interface ConnectorDefinition {
  key: string;
  name: string;
  category: string;
  direction: string;
  transport: string;
  status: 'available' | 'beta' | 'internal' | string;
}

export interface IntegrationConnection {
  id: string;
  name: string;
  connector_key: string;
  category?: string;
  direction?: string;
  status: 'draft' | 'active' | 'paused' | 'disabled' | 'error' | string;
  last_sync_at?: string | null;
  last_error?: string | null;
}

export interface SyncRun {
  id: string;
  trigger: 'manual' | 'schedule' | 'api' | string;
  status: 'running' | 'completed' | 'completed_with_errors' | 'failed' | string;
  rows_total?: number | null;
  rows_ok?: number | null;
  rows_failed?: number | null;
  started_at?: string | null;
  finished_at?: string | null;
  error?: string | null;
}

/** The employee roster CSV export connector (the only exportable one in v1). */
export const CSV_EXPORT_EMPLOYEES_KEY = 'csv_export_employees';
/** The scheduled-import connector (pulls a hosted CSV / Google Sheet / REST source). */
export const REST_PULL_KEY = 'rest_pull';
/** The SFTP inbound connector (polls a CSV from a remote SFTP server). */
export const SFTP_INBOUND_KEY = 'sftp_inbound';
/** The SFTP export-push connector (pushes the roster CSV to a remote SFTP path). */
export const SFTP_EXPORT_KEY = 'sftp_export_employees';
/** Udemy Business learning connector (installs via the Marketplace schema form;
 *  syncs courses + completions into learning-service). */
export const UDEMY_KEY = 'udemy';
/** Connectors that import employees on a schedule (rest_pull + sftp_inbound). */
export const isImportConnector = (key: string): boolean => key === REST_PULL_KEY || key === SFTP_INBOUND_KEY;
/** Connectors that export employees on a schedule (roster download + SFTP push). */
export const isExportConnector = (key: string): boolean => key === CSV_EXPORT_EMPLOYEES_KEY || key === SFTP_EXPORT_KEY;

const INT_BASE = '/api/v1/integrations';

export interface FieldMapping { source_field: string; target_field: string; transform?: string }
export interface MappingSet { id: string; name: string; mappings: FieldMapping[] }

/** Save a reusable source→target field mapping on a connection (the active/newest set is used). */
export const createMappingSet = async (id: string, name: string, mappings: FieldMapping[]): Promise<void> => {
  await gatewayApi.post(`${INT_BASE}/connections/${id}/mapping-sets`, { name, mappings });
};
export const listMappingSets = async (id: string): Promise<MappingSet[]> => {
  const { data } = await gatewayApi.get<any>(`${INT_BASE}/connections/${id}/mapping-sets`);
  return data.mapping_sets ?? data ?? [];
};
/** Enqueue an immediate sync run (manual trigger for import/export connections). */
export const enqueueSync = async (id: string): Promise<void> => {
  await gatewayApi.post(`${INT_BASE}/connections/${id}/sync`);
};

export interface TransformPreviewResult { input: string; output: string; error?: string }
export interface TransformPreviewResponse { valid: boolean; error?: string; results: TransformPreviewResult[] }

/** Compile + run a transform DSL expression over sample values (never mutates). */
export const previewTransform = async (transform: string, samples: string[]): Promise<TransformPreviewResponse> => {
  const { data } = await gatewayApi.post<TransformPreviewResponse>(`${INT_BASE}/transform/preview`, { transform, samples });
  return data;
};

export interface RunValidationError { row_no: number; field?: string; code?: string; message: string }

/** The per-row validation errors recorded for a sync run. */
export const getRunErrors = async (connectionId: string, runId: string): Promise<RunValidationError[]> => {
  const { data } = await gatewayApi.get<{ errors: RunValidationError[] }>(
    `${INT_BASE}/connections/${connectionId}/runs/${runId}/errors`,
  );
  return data.errors || [];
};

export const getIntegrationCatalog = async (): Promise<ConnectorDefinition[]> => {
  // The API wraps the list as {connectors: [...]} (older builds read
  // `definitions`, which never existed — the catalog silently parsed empty).
  const { data } = await gatewayApi.get<any>(`${INT_BASE}/catalog`);
  return data.connectors || data.definitions || [];
};

export const listIntegrationConnections = async (): Promise<IntegrationConnection[]> => {
  const { data } = await gatewayApi.get<any>(`${INT_BASE}/connections`);
  return (Array.isArray(data) ? data : data.connections) || [];
};

export const createIntegrationConnection = async (input: {
  name: string;
  connector_key: string;
  config?: Record<string, any>;
  credentials?: Record<string, any>;
}): Promise<IntegrationConnection> => {
  const { data } = await gatewayApi.post<any>(`${INT_BASE}/connections`, {
    name: input.name,
    connector_key: input.connector_key,
    config: input.config ?? {},
    credentials: input.credentials ?? {},
  });
  return (data.connection ?? data) as IntegrationConnection;
};

export const patchIntegrationConnection = async (
  id: string,
  patch: { status: string },
): Promise<void> => {
  await gatewayApi.patch(`${INT_BASE}/connections/${id}`, patch);
};

export const listConnectionRuns = async (id: string): Promise<SyncRun[]> => {
  const { data } = await gatewayApi.get<{ runs: SyncRun[] }>(`${INT_BASE}/connections/${id}/runs`);
  return data.runs || [];
};

export interface ConnectionSchedule { cron: string; timezone: string; enabled: boolean }

/** Read a connection's cron schedule (null if none). */
export const getConnectionSchedule = async (id: string): Promise<ConnectionSchedule | null> => {
  const { data } = await gatewayApi.get<{ schedule: ConnectionSchedule | null }>(`${INT_BASE}/connections/${id}/schedule`);
  return data.schedule ?? null;
};

/** Create/update (or disable) a connection's cron schedule. */
export const setConnectionSchedule = async (
  id: string,
  input: { cron: string; timezone?: string; enabled: boolean },
): Promise<ConnectionSchedule | null> => {
  const { data } = await gatewayApi.put<{ schedule: ConnectionSchedule | null }>(`${INT_BASE}/connections/${id}/schedule`, input);
  return data.schedule ?? null;
};

/** Download the CSV file a scheduled/async run produced (L2.3). */
export const downloadRunArtifact = async (connectionId: string, runId: string): Promise<Blob> => {
  const { data } = await gatewayApi.get(`${INT_BASE}/connections/${connectionId}/runs/${runId}/download`, {
    responseType: 'blob',
  });
  return data as Blob;
};

/** Run the CSV export and hand back the file blob. */
export const exportConnectionCsv = async (id: string): Promise<Blob> => {
  const { data } = await gatewayApi.post(`${INT_BASE}/connections/${id}/export`, undefined, {
    responseType: 'blob',
  });
  return data as Blob;
};

// ---- Marketplace (Phase 4) --------------------------------------------------

/** A JSON schema field descriptor as carried by config_schema/credential_schema. */
export interface SchemaField {
  key: string;
  type?: string; // string | secret | ...
  required?: boolean;
  label?: string;
}
export interface ConnectorSchema {
  fields?: SchemaField[];
}

/** A marketplace listing: a connector definition + adoption/review stats. */
export interface MarketplaceEntry {
  key: string;
  name: string;
  category: string;
  direction: string;
  transport: string;
  status: string;
  docs_url?: string;
  target_key?: string;
  verified: boolean;
  config_schema?: ConnectorSchema;
  credential_schema?: ConnectorSchema;
  install_count: number;
  review_count: number;
  avg_rating?: number | null;
  my_rating?: number | null;
}

export interface ConnectorReview {
  id: string;
  connector_key: string;
  rating: number;
  comment?: string;
  created_at: string;
  updated_at: string;
}

/** The offered connector catalog enriched with install/review stats + verified badge. */
export const getMarketplace = async (): Promise<MarketplaceEntry[]> => {
  const { data } = await gatewayApi.get<{ connectors: MarketplaceEntry[] }>(`${INT_BASE}/marketplace`);
  return data.connectors || [];
};

/** A connector's reviews (newest first). */
export const getMarketplaceReviews = async (key: string): Promise<ConnectorReview[]> => {
  const { data } = await gatewayApi.get<{ reviews: ConnectorReview[] }>(
    `${INT_BASE}/marketplace/${encodeURIComponent(key)}/reviews`,
  );
  return data.reviews || [];
};

/** Record/update the tenant's review of a connector (rating 1-5). */
export const postMarketplaceReview = async (
  key: string,
  rating: number,
  comment?: string,
): Promise<ConnectorReview> => {
  const { data } = await gatewayApi.post<ConnectorReview>(
    `${INT_BASE}/marketplace/${encodeURIComponent(key)}/reviews`,
    { rating, comment: comment || '' },
  );
  return data;
};

// ---------------------------------------------------------------- Asana (worksync)
// Tenant-level Asana work-management integration. OAuth + config live in
// migration-service; tokens never reach the frontend.

export interface AsanaConfig {
  workspace_gid?: string;
  workspace_name?: string;
  outbound_enabled?: boolean;
  inbound_enabled?: boolean;
  /** lifecycle type -> package name (or "*") -> Asana project GID */
  projects?: Record<string, Record<string, string>>;
  project_names?: Record<string, string>;
  project_urls?: Record<string, string>;
  /** lifecycle type -> package name -> delegated task keys */
  delegation?: Record<string, Record<string, string[]>>;
}

export interface AsanaWebhookView { project_gid: string; status: string }

export interface AsanaStatus {
  connected: boolean;
  status: string;
  needs_reauthorization: boolean;
  workspace_gid?: string;
  workspace_name?: string;
  config: AsanaConfig;
  webhooks: AsanaWebhookView[] | null;
  last_sync_at?: string | null;
  last_error?: string;
  connection_id?: string;
}

export interface AsanaWorkspace { gid: string; name: string }

export interface AsanaTaskLink { state: string; url?: string }

export interface AsanaLifecycleLinks {
  connected: boolean;
  instance_url?: string;
  delegated_task_keys: string[];
  tasks: Record<string, AsanaTaskLink>;
}

export const getAsanaStatus = async (): Promise<AsanaStatus> => {
  const { data } = await gatewayApi.get<AsanaStatus>(`${INT_BASE}/asana/status`);
  return data;
};

/** Returns the Asana consent URL; the caller navigates the browser to it.
 * Sends the current SPA URL (tenant-prefixed) so OAuth lands back here —
 * the backend accepts it only when the origin matches the configured app. */
export const startAsanaOAuth = async (): Promise<string> => {
  const { data } = await gatewayApi.post<{ url: string }>(`${INT_BASE}/asana/oauth/start`, {
    return_to: window.location.href,
  });
  return data.url;
};

export const listAsanaWorkspaces = async (): Promise<AsanaWorkspace[]> => {
  const { data } = await gatewayApi.get<{ workspaces: AsanaWorkspace[] }>(`${INT_BASE}/asana/workspaces`);
  return data.workspaces || [];
};

export const updateAsanaConfig = async (patch: AsanaConfig): Promise<AsanaConfig> => {
  const { data } = await gatewayApi.put<{ config: AsanaConfig }>(`${INT_BASE}/asana/config`, patch);
  return data.config;
};

export const disconnectAsana = async (): Promise<void> => {
  await gatewayApi.post(`${INT_BASE}/asana/disconnect`);
};

/** Per-instance Asana links + delegation for the HR lifecycle card. */
export const getAsanaLifecycleLinks = async (
  instanceId: string, lifecycleType: string, packageName: string, taskIds: string[],
): Promise<AsanaLifecycleLinks> => {
  const params = new URLSearchParams({ type: lifecycleType, package: packageName });
  if (taskIds.length) params.set('task_ids', taskIds.join(','));
  const { data } = await gatewayApi.get<AsanaLifecycleLinks>(
    `${INT_BASE}/asana/lifecycle/${instanceId}?${params.toString()}`,
  );
  return data;
};

// ---- Asana assignment sync (Workforce Execution Phase 4) --------------------

export interface AssignProfileProject { gid: string; name?: string; url?: string }

export interface AsanaAssignProfile {
  direction: 'off' | 'inbound' | 'bidirectional';
  projects: AssignProfileProject[] | null;
  typeKey: string;
  outboundEvents: string[] | null;
  lastReconcile?: Record<string, string> | null;
}

export interface AsanaIdentityLink {
  id: string;
  externalUserId: string;
  externalEmail?: string;
  externalName?: string;
  employeeId?: string;
  matchMethod: string;
  status: 'active' | 'unmapped' | 'ambiguous' | 'orphaned';
  updatedAt: string;
}

export const getAsanaAssignProfile = async (): Promise<AsanaAssignProfile> => {
  const { data } = await gatewayApi.get<AsanaAssignProfile>(`${INT_BASE}/asana/assignments/profile`);
  return data;
};

export const updateAsanaAssignProfile = async (patch: {
  direction: string; projectGids: string[]; typeKey?: string; outboundEvents?: string[];
}): Promise<AsanaAssignProfile> => {
  const { data } = await gatewayApi.put<AsanaAssignProfile>(`${INT_BASE}/asana/assignments/profile`, patch);
  return data;
};

export const listAsanaIdentities = async (status?: string): Promise<AsanaIdentityLink[]> => {
  const qs = status ? `?status=${status}` : '';
  const { data } = await gatewayApi.get<{ identities: AsanaIdentityLink[] }>(
    `${INT_BASE}/asana/assignments/identities${qs}`,
  );
  return data.identities || [];
};

export const mapAsanaIdentity = async (linkId: string, employeeId: string, userId?: string): Promise<AsanaIdentityLink> => {
  const { data } = await gatewayApi.post<AsanaIdentityLink>(
    `${INT_BASE}/asana/assignments/identities/${linkId}/map`, { employeeId, userId },
  );
  return data;
};

export const reconcileAsanaAssignments = async (): Promise<void> => {
  await gatewayApi.post(`${INT_BASE}/asana/assignments/reconcile`);
};

// ---- Dana external MCP servers (Agent Platform 9F) --------------------------

export interface DanaMcpServer {
  name: string;
  url: string;
  enabled: boolean;
  hasToken: boolean;
  tokenLast4?: string | null;
}

export const getDanaMcpServers = async (): Promise<DanaMcpServer[]> => {
  const { data } = await gatewayApi.get<{ servers: DanaMcpServer[] }>(
    '/api/v1/tenants/dana-config/mcp-servers',
  );
  return data.servers || [];
};

/** Full-replace; an entry without `token` keeps its stored one. */
export const setDanaMcpServers = async (
  servers: Array<{ name: string; url: string; token?: string | null; enabled?: boolean }>,
): Promise<DanaMcpServer[]> => {
  const { data } = await gatewayApi.put<{ servers: DanaMcpServer[] }>(
    '/api/v1/tenants/dana-config/mcp-servers', { servers },
  );
  return data.servers || [];
};

// ---- Approval grants (Agent Platform 9D) ------------------------------------

/** Mint a single-use approval grant for one approval-tier action. The server
 * verifies the CALLER holds the capability's required permission (e.g. a
 * payroll approver for payroll.run.approve). Shown once, expires in 10 min. */
export const mintApprovalGrant = async (input: {
  capabilityId: string;
  resourceId: string;
}): Promise<{ grant: string; expiresIn: number }> => {
  const { data } = await gatewayApi.post<{ grant: string; expiresIn: number }>(
    `${DEV_BASE}/approval-grants`, input,
  );
  return data;
};

export interface ApprovalGrantRow {
  id: string;
  capabilityId: string;
  resourceId: string;
  approvedBy: string;
  mine: boolean;
  createdAt: string;
  expiresAt: string;
  status: 'active' | 'used' | 'expired' | 'revoked';
}

export const listApprovalGrants = async (): Promise<ApprovalGrantRow[]> => {
  const { data } = await gatewayApi.get<{ grants: ApprovalGrantRow[] }>(`${DEV_BASE}/approval-grants`);
  return data.grants || [];
};

export const revokeApprovalGrant = async (id: string): Promise<void> => {
  await gatewayApi.post(`${DEV_BASE}/approval-grants/${id}/revoke`);
};

// ---- AI action requests (v2.1 Phase 1c: review page) -----------------------
// An external AI (ChatGPT, Claude…) asked to change something as this person.
// Only that person, signed in here, can approve or decline it.

export interface AiActionRequest {
  id: string;
  tool: string;
  summary: string;
  args: Record<string, unknown>;
  status: 'pending' | 'approved' | 'declined' | 'used' | 'expired';
  clientId?: string | null;
  createdAt: string;
  expiresAt: string;
  decidedAt?: string | null;
}

export const getAiActionRequest = async (id: string): Promise<AiActionRequest> => {
  const { data } = await gatewayApi.get<AiActionRequest>(`${DEV_BASE}/ai-action-requests/${id}`);
  return data;
};

export const decideAiActionRequest = async (id: string, approve: boolean): Promise<AiActionRequest> => {
  const { data } = await gatewayApi.post<AiActionRequest>(
    `${DEV_BASE}/ai-action-requests/${id}/${approve ? 'approve' : 'decline'}`);
  return data;
};

// ---- Capability registry (Agent Platform Phase 5) ---------------------------

export interface Capability {
  id: string;
  title: string;
  domain: string;
  surfaces: Record<string, boolean>;
  required_scope: string | null;
  required_permission: string | null;
  risk: 'read' | 'low' | 'approval' | 'financial' | string;
  mutating: boolean;
  approval_required: boolean;
  sensitive: string;
  status: string;
}

export const listCapabilities = async (filter?: {
  domain?: string; surface?: string; status?: string; risk?: string;
}): Promise<Capability[]> => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter || {})) if (v) params.set(k, v);
  const qs = params.toString() ? `?${params}` : '';
  const { data } = await gatewayApi.get<{ capabilities: Capability[] }>(`${DEV_BASE}/capabilities${qs}`);
  return data.capabilities || [];
};

// ---- MCP analytics + tenant delegated sessions (universal-LLM Phase 5) -----

export interface McpToolStat {
  tool: string;
  mutating: boolean;
  calls: number;
  errors: number;
  needsConfirmation: number;
  maxLatencyMs: number;
  avgLatencyMs: number;
  lastCalledAt: string;
}

export interface McpAnalytics {
  since: string;
  tools: McpToolStat[] | null;
  byClient: Record<string, number>;
  total: number;
  mutations: number;
}

/** Live per-tenant MCP tool-call aggregates (in-memory on the MCP server —
 * `since` says when counters last reset; the audit stream is the durable record). */
export const getMcpAnalytics = async (): Promise<McpAnalytics> => {
  const { data } = await gatewayApi.get<McpAnalytics>(`${DEV_BASE}/mcp-analytics`);
  return data;
};

export interface TenantAuthorization extends MyAuthorization {
  userEmail: string;
}

/** Tenant-wide delegated AI/app sessions (admin): who linked what, with what scopes. */
export const listTenantAuthorizations = async (): Promise<TenantAuthorization[]> => {
  const { data } = await gatewayApi.get<{ authorizations: TenantAuthorization[] }>(
    `${APP_BASE}/authorizations/all`,
  );
  return data.authorizations || [];
};

export const revokeTenantAuthorization = async (familyId: string): Promise<void> => {
  await gatewayApi.post(`${APP_BASE}/authorizations/${familyId}/revoke-any`);
};
